/**
 * Export-side paper-space layout reconciliation (#314 redesign).
 *
 * # The problem this solves
 *
 * The importer stores each paper tab's entities in `document.spaces[]`
 * (`kind: "layout"`). For an AutoCAD-authored multi-layout DXF the entities
 * physically live in TWO places in the source file:
 *
 *   - the ENTITIES section — `*Model_Space` content + the FIRST/active
 *     `*Paper_Space` (un-suffixed) content, and
 *   - the `*Paper_Space<N>` BLOCK definitions — the EXTRA paper tabs.
 *
 * On export the writer must reproduce THAT byte-faithful placement. A naive
 * exporter that dumps EVERY layout space into the ENTITIES section (tagged with
 * group 67 + group 410 layout name) re-homes every extra tab's content onto the
 * FIRST paperspace from AutoCAD/ezdxf's point of view — ezdxf reports e.g.
 * `Plan block=*Paper_Space count=9` with `Elevation`/`Detail` empty, because the
 * physical ENTITIES section is owned by the active paperspace block_record. The
 * fix: emit the extra tabs ONCE, into their own `*Paper_Space<N>` BLOCK
 * definition, and EXCLUDE them from the ENTITIES loop.
 *
 * # Canonical target structure (ezdxf-verified on multi-layout-complex.dxf)
 *
 *   - Model space               → ENTITIES (group 67 absent).
 *   - FIRST/active paper space   → ENTITIES (group 67 = 1). Its `*Paper_Space`
 *     block stays EMPTY. (Do NOT relocate it — that would break imported
 *     byte-faithfulness and is not what AutoCAD writes.)
 *   - EXTRA paper tabs           → ONLY in their `*Paper_Space<N>` BLOCK
 *     definition; EXCLUDED from the ENTITIES loop.
 *   - `*Model_Space` block       → EMPTY children (model content is canonical
 *     in ENTITIES).
 *
 * # Pairing each layout space to its `*Paper_Space<N>` block name
 *
 * Both the imported path and the from-scratch (conformant-skeleton) path leave
 * the same structure on the document:
 *
 *   - `document.spaces[]` carries the entities (the empty `*Paper_Space<N>`
 *     block definitions are present in `document.blocks` but EMPTY), and
 *   - the LAYOUT ↔ BLOCK_RECORD handle chain links a layout space to its block
 *     record name:
 *         `layoutsBySpaceId[space.id].ownerBlockRecordHandle`
 *           (or `layouts[space.name].ownerBlockRecordHandle`)
 *         === `blockRecords[name].handle`
 *         === the `*Paper_Space<N>` block name.
 *
 * We pair STRICTLY by that HANDLE CHAIN (suffix-agnostic — it works for the
 * canonical `*Paper_Space0/1/…` sequence AND for arbitrary suffixes like
 * `*Paper_Space109`/`*Paper_Space200` that an imported file may carry). There is
 * deliberately NO positional / synthetic-name fallback (#314 round-2, Codex
 * F3/F4/F5): a layout space is relocated into a block ONLY when it resolves to a
 * RECORD-BACKED suffixed `*Paper_Space<N>` block name.
 *
 *   - Resolves to the un-suffixed `*Paper_Space` → it is the first/active paper
 *     (AutoCAD's invariant: the un-suffixed `*Paper_Space` block is always the
 *     active/first paperspace). STAYS in ENTITIES; its block stays empty.
 *   - Resolves to a suffixed `*Paper_Space<N>` → EXTRA tab → EXCLUDED from
 *     ENTITIES + rebuilt in its block. This holds REGARDLESS of whether an
 *     un-suffixed first paper exists (F5: a doc whose first paper LAYOUT was
 *     deleted still has suffixed tabs that are block-owned by their own records).
 *   - UNRESOLVED (no LAYOUT/BLOCK_RECORD wiring — a runtime-added layout via
 *     shell/add-space, or a hand-built doc with no blocks) → LEFT in ENTITIES.
 *     We do NOT synthesise a `*Paper_Space<N>` name: a synthetic name COLLIDES
 *     with real record-backed ones (F4) and has no BLOCK_RECORD owner, so the
 *     relocated content would be orphaned. The status-quo (keep in ENTITIES) is
 *     safe and count-stable.
 *
 * F3 corollary: if there are no `*Paper_Space*` BLOCK_RECORDs at all (no
 * `document.blocks` / `ensureConformant` OFF on a legacy doc), NOTHING resolves
 * → nothing is excluded → all paper stays in ENTITIES (status quo, no vanish).
 */

import { isLayoutBlockName, isPaperSpaceBlockName } from "./blockNaming";
import type { DrawingDocument, DrawingSpace } from "./model";

/** The first/active paper-space block name (un-suffixed). */
export const FIRST_PAPER_SPACE_BLOCK_NAME = "*Paper_Space";

/**
 * F4' (#314 round-2) — is `name` the FIRST/active (un-suffixed) `*Paper_Space`
 * block, CASE-INSENSITIVELY? `resolvePaperBlockName` returns the block name with
 * its ORIGINAL spelling out of `document.blockRecords` (the importer keeps the
 * source-file casing), so an AutoCAD-folded `*PAPER_SPACE` or a hand-built
 * `*paper_space` must still be recognised as the first paper (stays in ENTITIES)
 * rather than mis-classified as a suffixed extra tab. AutoCAD treats block names
 * case-insensitively (blockNaming.ts predicates do too), so the un-suffixed-paper
 * test here matches that: exactly `*Paper_Space` with NO trailing digits, folded.
 */
export function isFirstPaperSpaceBlockName(name: string): boolean {
  return name.toLowerCase() === FIRST_PAPER_SPACE_BLOCK_NAME.toLowerCase();
}

export type PaperSpaceLayoutPlan = {
  /**
   * The layout space whose handle chain resolves to the un-suffixed
   * `*Paper_Space` block (the first/active paper). STAYS in the ENTITIES
   * section. `undefined` when no layout space resolves to `*Paper_Space` (a
   * model-only document, OR an F5 doc whose first paper LAYOUT was deleted).
   */
  firstPaperSpace: DrawingSpace | undefined;
  /**
   * EXTRA paper tabs, keyed by their RECORD-BACKED suffixed `*Paper_Space<N>`
   * block name (resolved via the handle chain). Each is EXCLUDED from ENTITIES
   * and emitted ONCE into its block.
   */
  paperSpaceByBlockName: Map<string, DrawingSpace>;
  /**
   * Space ids of the EXTRA paper tabs — the set the ENTITIES loop excludes.
   * ONLY spaces that resolve to a record-backed suffixed `*Paper_Space<N>` are
   * here. The first/active paper (un-suffixed) and any UNRESOLVED layout space
   * (no LAYOUT/BLOCK_RECORD wiring) are intentionally NOT in this set: they keep
   * emitting to ENTITIES.
   */
  excludedSpaceIds: Set<string>;
};

/**
 * Resolve the `*Paper_Space<N>` block name owning a given layout space via the
 * LAYOUT ↔ BLOCK_RECORD handle chain. Returns `undefined` when the chain is not
 * wired (the caller then falls back to spaces[] index ordering).
 *
 * F1'' (#314 round-3) — the owner handle is resolved from EITHER source,
 * preferring whichever carries a NON-EMPTY `ownerBlockRecordHandle`. A plain
 * `layoutsBySpaceId?.[space.id] ?? layouts?.[space.name]` STOPS at the by-id
 * entry even when that entry's owner is EMPTY: that is exactly the FROM-SCRATCH
 * `shell/add-space` state — the runtime `buildDefaultLayout` entry (empty owner)
 * is seeded into BOTH maps, then `ensureConformantSkeleton` mints the REAL
 * BLOCK_RECORD handle into `document.layouts[space.name]` (read by space NAME,
 * line ~416 of dxfBridge.conformantSkeleton.ts) yet leaves the by-id map STALE
 * (it is keyed by id and never re-synced from `finalLayouts`). A `??`-only lookup
 * would read the stale empty-owner by-id entry → `undefined` → the extra tab is
 * NOT excluded → it serialises in ENTITIES (re-homing / data-loss) on conformant
 * from-scratch saves. Preferring the non-empty owner from either map fixes this
 * (and the symmetric case where only the by-id map is wired).
 */
export function resolvePaperBlockName(
  document: DrawingDocument,
  space: DrawingSpace,
): string | undefined {
  const byId = document.layoutsBySpaceId?.[space.id];
  const byName = document.layouts?.[space.name];
  const byIdOwner = byId?.ownerBlockRecordHandle?.trim();
  const byNameOwner = byName?.ownerBlockRecordHandle?.trim();
  // Prefer whichever source carries a NON-EMPTY owner handle. `??`-style
  // precedence (by-id first) is kept ONLY as the tie-break when BOTH are set.
  const ownerHandle = byIdOwner || byNameOwner;
  if (!ownerHandle) return undefined;
  for (const [name, record] of Object.entries(document.blockRecords ?? {})) {
    if (!isPaperSpaceBlockName(name)) continue;
    if ((record.handle ?? "").trim().toLowerCase() === ownerHandle.toLowerCase()) {
      return name;
    }
  }
  return undefined;
}

/**
 * Compute the export placement plan for the document's layout (paper) spaces.
 *
 * The model space is never returned here — it always emits to ENTITIES and its
 * `*Model_Space` block is emptied by the blocks builder.
 */
export function buildPaperSpaceLayoutPlan(
  document: DrawingDocument,
): PaperSpaceLayoutPlan {
  const layoutSpaces = document.spaces.filter((s) => s.kind === "layout");

  const paperSpaceByBlockName = new Map<string, DrawingSpace>();
  const excludedSpaceIds = new Set<string>();
  let firstPaperSpace: DrawingSpace | undefined;

  // HANDLE-CHAIN-ONLY (#314 round-2). Resolve each layout space's owning
  // `*Paper_Space<N>` block name via the LAYOUT ↔ BLOCK_RECORD handle chain.
  // The block name's SUFFIX classifies the space deterministically — there is
  // NO positional / synthetic-name fallback (that collided with real records
  // and orphaned content; F3/F4/F5).
  for (const space of layoutSpaces) {
    const resolved = resolvePaperBlockName(document, space);
    if (resolved !== undefined && isFirstPaperSpaceBlockName(resolved)) {
      // Un-suffixed `*Paper_Space` (case-insensitive — F4') ⇒ the first/active
      // paper. STAYS in ENTITIES. First wins (AutoCAD has exactly one active
      // paperspace block_record).
      if (!firstPaperSpace) firstPaperSpace = space;
      continue;
    }
    if (resolved !== undefined) {
      // Suffixed `*Paper_Space<N>`, record-backed ⇒ EXTRA tab. EXCLUDED from
      // ENTITIES + rebuilt in its block. Holds even when no first paper exists
      // (F5: deleted un-suffixed LAYOUT, suffixed tabs still block-owned).
      paperSpaceByBlockName.set(resolved, space);
      excludedSpaceIds.add(space.id);
      continue;
    }
    // UNRESOLVED — no LAYOUT/BLOCK_RECORD wiring (runtime-added layout via
    // shell/add-space, or a hand-built doc with no blocks). LEAVE in ENTITIES.
    // Do NOT synthesise a `*Paper_Space<N>` name (it collides with real
    // record-backed ones and has no record owner ⇒ orphaned content; F4).
  }

  return { firstPaperSpace, paperSpaceByBlockName, excludedSpaceIds };
}

/**
 * F2'' (#314 round-3) — CASE-INSENSITIVE pairing lookup into
 * `paperSpaceByBlockName`. The map is keyed by the BLOCK_RECORD name spelling
 * (`resolvePaperBlockName` returns the `document.blockRecords` key verbatim, to
 * preserve the source-file casing on emit), but the BLOCKS-section per-block loop
 * and the annotative export key by the BLOCK-DEFINITION `name` spelling
 * (`document.blocks` key). AutoCAD folds block names case-insensitively, so a DXF
 * may legitimately carry DIFFERENT casing for the record (e.g. `*Paper_Space0`)
 * vs the block definition (`*PAPER_SPACE0`). A spelling-exact `.get(name)` then
 * MISSES → the block is treated as unpaired (kept intact) WHILE its space was
 * already excluded from ENTITIES by the plan → the edited tab's entities are
 * dropped / a second case-variant block is synthesized post-loop. Matching the
 * case-insensitive `isPaperSpaceBlockName` / `isLayoutBlockName` /
 * `isFirstPaperSpaceBlockName` predicates, this folds both sides before
 * comparing. Returns the paired space, or `undefined` when none matches.
 *
 * O(n) over the (small) extra-tab set — keyed by record-name spelling we cannot
 * change without breaking emit fidelity + the `.keys()`/`.get(<exact>)` callers.
 */
export function findPairedPaperSpace(
  paperSpaceByBlockName: ReadonlyMap<string, DrawingSpace>,
  blockName: string,
): DrawingSpace | undefined {
  return findPairedPaperSpaceEntry(paperSpaceByBlockName, blockName)?.space;
}

/**
 * #314 round-4 — the case-insensitive pairing lookup but returning BOTH the
 * paired space AND the CANONICAL BLOCK_RECORD-backed name (the
 * `paperSpaceByBlockName` KEY that matched). The BLOCKS-section emit path needs
 * the canonical KEY — not the BLOCK-DEFINITION `name` it looked up with — so it
 * EMITS the relocated paper block under the record-backed spelling.
 *
 * Why this matters (round-4 P2): the importer (`dxfBridge.spaces.ts` Pass 2)
 * routes paper-space BLOCK content to its layout space CASE-SENSITIVELY
 * (`draftByBlockName.get(block.name)`, where `draftByBlockName` is keyed by the
 * BLOCK_RECORD name). If the export emitted the rebuilt paper block under the
 * `document.blocks` DEFINITION-key spelling (e.g. `*PAPER_SPACE0`) while the
 * BLOCK_RECORD / LAYOUT owner is `*Paper_Space0`, a save→re-import would MISS the
 * routing and DROP that tab's block-owned geometry — and the LAYOUT/BLOCK_RECORD
 * references would dangle (they name the canonical record). Emitting the canonical
 * KEY keeps (a) the case-sensitive importer happy and (b) the layout↔block linkage
 * in sync. Returns `undefined` when no key pairs (the caller keeps the block's own
 * `name`, i.e. the unpaired path — no normalization).
 */
/**
 * T9b review round 2 (P2) — does the BLOCKS-section export emit THIS block's
 * OWN typed children (`block.entities`)? The single predicate behind the
 * `buildBlocksSectionFromDrawingBlocks` branch plan, shared with every pass
 * that pre-scans "the entities the export will emit" (anonymous `*D<n>`
 * dimension blocks, referenced STYLE records, the conformant-skeleton entity
 * remint) so none of them can walk children the exporter drops (which minted
 * orphan `*D` blocks / STYLE records and advanced `$HANDSEED` on every save):
 *
 *   - a REGULAR user block → yes (its own def is the edit-authoritative copy);
 *   - `*Model_Space` → no (its supported content is canonical in ENTITIES);
 *   - a `*Paper_Space<N>` PAIRED with a layout space (handle chain resolved) →
 *     no (rebuilt from the paired SPACE, which the ENTITIES/space walk covers);
 *   - the un-suffixed first `*Paper_Space` → no (canonical in ENTITIES);
 *   - an UNPAIRED suffixed `*Paper_Space<N>` (broken/missing chain) → yes (its
 *     content exists only on the block def — F3').
 */
export function blockEmitsOwnTypedChildren(
  blockName: string,
  paperSpaceByBlockName: ReadonlyMap<string, DrawingSpace>,
): boolean {
  if (!isLayoutBlockName(blockName)) return true;
  if (!isPaperSpaceBlockName(blockName)) return false; // *Model_Space
  if (findPairedPaperSpaceEntry(paperSpaceByBlockName, blockName)) return false;
  return !isFirstPaperSpaceBlockName(blockName);
}

export function findPairedPaperSpaceEntry(
  paperSpaceByBlockName: ReadonlyMap<string, DrawingSpace>,
  blockName: string,
): { canonicalName: string; space: DrawingSpace } | undefined {
  const direct = paperSpaceByBlockName.get(blockName);
  if (direct !== undefined) return { canonicalName: blockName, space: direct };
  const folded = blockName.toLowerCase();
  for (const [key, space] of paperSpaceByBlockName) {
    if (key.toLowerCase() === folded) return { canonicalName: key, space };
  }
  return undefined;
}

/**
 * F2'' (#314 round-3) — CASE-INSENSITIVE membership in `paperSpaceByBlockName`,
 * the predicate form of {@link findPairedPaperSpace}. Used where a caller only
 * needs to know whether `blockName` is a PAIRED extra tab (e.g. the annotative
 * export's `isLayoutBlockEmittedViaSpace`), independent of the record-vs-block
 * name casing.
 */
export function hasPairedPaperSpace(
  paperSpaceByBlockName: ReadonlyMap<string, DrawingSpace>,
  blockName: string,
): boolean {
  return findPairedPaperSpace(paperSpaceByBlockName, blockName) !== undefined;
}
